import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Award, Check, Code, Globe, Headphones, Layers, MapPin, Package, Plus, Receipt, ShieldCheck, Users, Wrench, Zap } from 'lucide-react';
import { dateLabel } from '@adisys/shared';
import { qtyLabel } from '../../lib/crm';

const fmt = (n: number | string) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n ?? 0));

const NAVY = '#0B1F3A';
const BLUE = '#1560BD';
const SKY = '#2F9BF4';
const LINE = '#D9DFE6';
const MUTED = '#5B6878';
const SOFT = '#F3F5F8';

const COND: CSSProperties = { fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif" };
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;700&family=Barlow+Condensed:wght@500;600;700&display=swap');
.proposal-sheet{font-family:Barlow,system-ui,sans-serif}
.proposal-sheet .avoid{break-inside:avoid}
@media screen{.proposal-sheet td>section+section{margin-top:18mm;padding-top:10mm;border-top:1px dashed ${LINE}}}`;

const CUSTOMER_LOGOS = [
  'p5-2', 'p5-3', 'p5-4', 'p5-5', 'p5-6', 'p5-7', 'p5-8', 'p5-19', 'p5-9', 'p5-10', 'p5-11', 'p5-12', 'p5-13', 'p5-14', 'p5-15', 'p5-16',
  'p5-17', 'p5-18', 'p5-0', 'p5-1', 'p6-3', 'p6-4', 'p6-2', 'p6-0', 'p6-5', 'p6-6', 'p6-7', 'p6-8', 'p6-13', 'p6-1', 'p6-10', 'p6-11',
  'p6-12', 'p6-14', 'p7-0', 'p7-1', 'p7-2', 'p7-3', 'p7-4', 'p7-5', 'p7-6', 'p7-7', 'p7-8', 'p7-9',
];
const HIGHLIGHT_ICONS = [Award, Users, Zap, Headphones, Code, Globe];
const SUPPORT_ICONS = [ShieldCheck, Plus, MapPin, Wrench, Zap];

const breakBefore = { breakBefore: 'page' } as const;

/** Section opener: "05 / Eyebrow" over a condensed upper-case title. */
function Heading({ n, eyebrow, title }: { n: number; eyebrow: string; title: string }) {
  return (
    <div className="mb-5 flex flex-col gap-1.5">
      <div style={{ ...COND, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', color: BLUE }}>
        {String(n).padStart(2, '0')} &nbsp;/&nbsp; {eyebrow}</div>
      <h2 style={{ ...COND, fontWeight: 700, fontSize: 30, lineHeight: 1.02, margin: 0, color: NAVY, textTransform: 'uppercase' }}>{title}</h2>
    </div>
  );
}
const Label = ({ children }: { children: ReactNode }) => (
  <div className="mb-2.5" style={{ ...COND, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', color: BLUE }}>{children}</div>
);
const Stat = ({ value, label, note }: { value: ReactNode; label: string; note?: string }) => (
  <div className="flex flex-1 flex-col items-center gap-1 px-3 py-3.5 text-center" style={{ border: `1px solid ${LINE}` }}>
    <div style={{ ...COND, fontWeight: 700, fontSize: 36, lineHeight: 1, color: NAVY }}>{value}</div>
    <div style={{ ...COND, fontWeight: 600, fontSize: 14, letterSpacing: '.08em', textTransform: 'uppercase', color: BLUE }}>{label}</div>
    {note && <div className="text-[10.5px] leading-snug" style={{ color: MUTED }}>{note}</div>}
  </div>
);
const IconRow = ({ icon: Icon, children }: { icon: typeof Check; children: ReactNode }) => (
  <div className="flex items-start gap-3 py-2.5" style={{ borderBottom: `1px solid ${LINE}` }}>
    <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: BLUE }} strokeWidth={1.5} aria-hidden />
    <div className="text-[12px] leading-snug">{children}</div>
  </div>
);
const Card = ({ icon: Icon, title, children }: { icon: typeof Check; title: string; children: ReactNode }) => (
  <div className="avoid pt-3" style={{ borderTop: `2px solid ${SKY}` }}>
    <div className="mb-1.5 flex items-center gap-2.5">
      <Icon className="h-5 w-5 shrink-0" style={{ color: BLUE }} strokeWidth={1.5} aria-hidden />
      <h3 style={{ ...COND, fontWeight: 700, fontSize: 18, textTransform: 'uppercase', color: NAVY, margin: 0 }}>{title}</h3>
    </div>
    <p className="text-[11.5px] leading-snug">{children}</p>
  </div>
);

/**
 * The customer-facing proposal, laid out like the ADISYS proposal template: cover, executive
 * summary, company profile, customers and partners, itemised offer, support and
 * scope, terms with the account manager's signature. Fixed company text comes from the
 * `crm.proposal` setting so management can edit it without a release.
 */
export function ProposalDocument({ q, amountInWords }: { q: any; amountInWords: (n: number) => string; readOnly?: boolean }) {
  const p = q.proposal ?? {};
  const c = q.customer ?? {};
  const co = q.company ?? {};
  const sp = q.salesperson;
  const name = co.name ?? 'ADISYS Technologies Private Limited';
  const lines: any[] = q.lines;
  const hasDiscount = lines.some((l) => Number(l.discount) > 0);
  const [noSig, setNoSig] = useState(false);
  // Browsers print the tab title in the page header, so name it after the proposal instead of the app.
  useEffect(() => {
    const prev = document.title;
    document.title = `${q.quoteNo} ${c.company ?? ''}`.trim();
    return () => { document.title = prev; };
  }, [q.quoteNo, c.company]);

  const logo = p.logoData || '/proposal/adisys-logo.png';
  const website = p.website || 'www.adisystech.com';
  const products = lines.filter((l) => l.kind === 'product');
  const units = products.reduce((s, l) => s + Number(l.quantity || 0), 0);
  const makes = [...new Set(products.map((l) => l.brandName).filter(Boolean))] as string[];
  const validDays = q.validUntil ? Math.max(0, Math.ceil((new Date(q.validUntil).getTime() - new Date(q.createdAt).getTime()) / 864e5)) : null;
  const subject = String(q.subject || q.project?.name || 'Commercial Offer');
  // Keep the title on one line: shrink to fit the 160 mm text column, wrapping only for very long titles.
  const titleSize = Math.max(30, Math.min(56, Math.floor(590 / (subject.length * 0.46))));
  // Sections 2-6 are fixed, so the offer, support and terms are 7, 8 and 9.
  const secOffer = 7;
  const conclusion = String(p.conclusion ?? '').replaceAll('{customer}', c.company ?? 'your organisation');
  const terms: { label: string; text: string }[] = [
    q.deliveryTerms && { label: 'Delivery', text: q.deliveryTerms },
    q.paymentTerms && { label: 'Payment', text: q.paymentTerms },
    ...(p.terms ?? []),
    q.validUntil && { label: 'Validity', text: `Until ${dateLabel(q.validUntil)}` },
    // The standard terms above already cover these; the quote's own free-text terms only fill in when none are configured.
    ...(p.terms?.length ? [] : String(q.terms ?? '').split('\n')).filter(Boolean).map((t: string) => ({ label: '', text: t })),
  ].filter(Boolean);

  return (
    <article className="proposal-sheet mx-auto my-6 max-w-[210mm] bg-white text-[12px] leading-relaxed shadow-card" style={{ color: '#1D2733' }}>
      <style>{FONTS}</style>

      {/* Cover */}
      <section className="relative overflow-hidden text-white" style={{ background: NAVY, height: '296mm', breakAfter: 'page' }}>
        <img src="/proposal/cover.png" alt="" className="absolute right-0 top-0 h-full w-[52%] object-cover object-top" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${NAVY} 0%, ${NAVY} 46%, rgba(11,31,58,.78) 66%, rgba(11,31,58,.35) 100%)` }} />
        <div className="absolute inset-0 flex flex-col justify-between px-[14mm] pb-[12mm] pt-[14mm]">
          <div className="flex items-start justify-between gap-4">
            <div className="inline-flex bg-white px-4 py-3"><img src={logo} alt={name} className="h-12 w-auto max-w-[220px] object-contain" /></div>
            {c.logo && <div className="inline-flex bg-white px-4 py-3"><img src={c.logo} alt={c.company} className="h-12 w-auto max-w-[200px] object-contain" /></div>}
          </div>
          <div className="flex w-[160mm] flex-col gap-5">
            <div style={{ ...COND, fontWeight: 600, fontSize: 15, letterSpacing: '.24em', textTransform: 'uppercase', color: SKY }}>{p.offerTitle || 'Commercial Offer'}</div>
            <h1 style={{ ...COND, fontWeight: 700, fontSize: titleSize, lineHeight: .98, margin: 0, textTransform: 'uppercase', whiteSpace: titleSize > 30 ? 'nowrap' : 'normal' }}>{subject}</h1>
            <div style={{ ...COND, fontWeight: 500, fontSize: 22, color: '#C7D0DB' }}>Technical &amp; Commercial Proposal</div>
            <div style={{ height: 2, width: 72, background: SKY }} />
            <dl className="grid gap-x-5 gap-y-2.5 text-[13px] leading-snug" style={{ gridTemplateColumns: 'auto 1fr' }}>
              <dt style={{ color: '#9FB0C4' }}>Customer</dt>
              <dd className="font-semibold">{String(c.company ?? '').toUpperCase()}
                {(c.billingAddress || c.city) && <span className="block font-normal" style={{ color: '#C7D0DB' }}>
                  {[...new Set([c.billingAddress, c.city, c.state, c.pincode].filter(Boolean))].join(', ')}</span>}</dd>
              <dt style={{ color: '#9FB0C4' }}>Project</dt><dd className="font-semibold">{q.project?.name}</dd>
              <dt style={{ color: '#9FB0C4' }}>Reference No</dt><dd className="font-semibold">{q.quoteNo}</dd>
              <dt style={{ color: '#9FB0C4' }}>Date</dt><dd className="font-semibold">{dateLabel(q.createdAt)}</dd>
              {q.validUntil && <><dt style={{ color: '#9FB0C4' }}>Valid until</dt><dd className="font-semibold">{dateLabel(q.validUntil)}</dd></>}
              <dt style={{ color: '#9FB0C4' }}>Prepared by</dt><dd className="font-semibold">{name}</dd>
            </dl>
          </div>
          <div className="flex items-end justify-between gap-6 text-[11.5px] leading-relaxed" style={{ color: '#C7D0DB' }}>
            <span className="max-w-[115mm]">{String(p.address ?? '').replace(/\)\s+(?=\S)/, '), ')}<br />{[p.phones && `Mob: ${p.phones}`, p.email].filter(Boolean).join('  |  ')}</span>
            <span className="shrink-0" style={{ ...COND, fontWeight: 600, fontSize: 17, letterSpacing: '.08em', color: '#fff' }}>{website}</span>
          </div>
        </div>
      </section>

      {/* Header and footer repeat on every printed page after the cover */}
      <table className="w-full border-collapse">
        <thead><tr><td className="px-[14mm] pb-5 pt-[8mm]">
          <div className="flex items-center justify-between pb-2.5" style={{ borderBottom: `1px solid ${LINE}` }}>
            <img src={logo} alt={name} className="h-8 w-auto max-w-[180px] object-contain" />
            <span className="text-[11px] tracking-wide" style={{ color: MUTED }}>{website}</span>
          </div>
        </td></tr></thead>
        <tfoot><tr><td className="px-[14mm] pb-[8mm] pt-3">
          <div className="pt-2 text-[10px] leading-snug" style={{ borderTop: `1px solid ${LINE}`, color: MUTED }}>
            <div className="flex justify-between gap-4"><span>{name} &nbsp;·&nbsp; Ref. {q.quoteNo}</span><span>Confidential</span></div>
            <div>{[(p.gstin || co.gstin) && `GSTIN: ${p.gstin || co.gstin}`, p.pan && `PAN: ${p.pan}`, p.cin && `CIN: ${p.cin}`,
              p.stateName && `State: ${p.stateName}${p.stateCode ? ` (${p.stateCode})` : ''}`].filter(Boolean).join('  |  ')}</div>
          </div>
        </td></tr></tfoot>
        <tbody><tr><td className="px-[14mm]">

          {/* 02 Executive summary */}
          <section>
            <Heading n={2} eyebrow="Executive summary" title="Solution overview" />
            <p className="mb-5 text-[13px] leading-relaxed"><b>Project objective.</b> {name} submits this offer for {subject} for {c.company}
              {c.city ? `, ${c.city}` : ''}. {p.budgetaryNote || 'Every line item is quoted as supply, installation, testing and commissioning.'}</p>
            <Label>Solution at a glance</Label>
            <div className="mb-6 flex gap-2.5">
              <Stat value={products.length} label="Line items" note="Itemised in the commercial offer" />
              <Stat value={qtyLabel(units)} label="Units supplied" note="Across all products" />
              {makes.length > 0 && <Stat value={makes.length} label={makes.length === 1 ? 'Make' : 'Makes'} note={makes.slice(0, 4).join(' · ')} />}
              {validDays !== null && <Stat value={validDays} label="Days valid" note={`Until ${dateLabel(q.validUntil)}`} />}
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              <Card icon={Package} title="Proposed solution">{subject} for {c.company}, supplied, installed, tested and commissioned by ADISYS.</Card>
              <Card icon={Layers} title="Products and makes">{makes.length ? `${makes.join(', ')}.` : 'As listed in the commercial offer.'} Specifications and quantities are itemised in section {secOffer}.</Card>
              <Card icon={Wrench} title="Installation, testing & commissioning">Every line item is quoted as supply, installation, testing and commissioning. On-site support continues after completion.</Card>
              <Card icon={Receipt} title="Commercials">Offer value INR {fmt(q.grandTotal)} including GST. Payment, delivery and warranty terms are set out in section {secOffer + 2}.</Card>
            </div>
          </section>

          {/* 03 About */}
          <section style={breakBefore}>
            <Heading n={3} eyebrow="ADISYS introduction" title="About ADISYS Technologies" />
            <div className="mb-5 flex gap-3">
              <Stat value="16+" label="Years" note="In Security & Automation domain" />
              <Stat value="1500+" label="Customers" note="Served across various industry verticals" />
              <Stat value="Pan India" label="Presence" note="With in-house R&D and Service Centre" />
            </div>
            <div className="mb-5 grid grid-cols-2 gap-x-8">
              {(p.highlights ?? []).map((h: string, i: number) => {
                const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length]!;
                return <IconRow key={h} icon={Icon}>{h}</IconRow>;
              })}
            </div>
            <div className="space-y-3 text-[12.5px] leading-relaxed">{(p.introduction ?? []).map((t: string, i: number) => <p key={i}>{t}</p>)}</div>
          </section>

          {/* 04 Why ADISYS */}
          <section style={breakBefore}>
            <Heading n={4} eyebrow="Why ADISYS should be considered for this project?" title="Why ADISYS" />
            <div className="grid grid-cols-2 gap-x-8 gap-y-5">
              {(p.whyAdisys ?? []).map((t: string, i: number) => (
                <div key={i} className="avoid pt-3" style={{ borderTop: `2px solid ${SKY}` }}>
                  <div style={{ ...COND, fontWeight: 700, fontSize: 22, color: BLUE }}>{String(i + 1).padStart(2, '0')}</div>
                  <p className="text-[12px] leading-snug">{t}</p>
                </div>))}
            </div>
          </section>

          {/* 05 Customers */}
          <section style={breakBefore}>
            <Heading n={5} eyebrow="Our esteemed customers" title="Our Esteemed Customers" />
            <div className="grid grid-cols-6 gap-2.5" style={{ gridAutoRows: '23mm' }}>
              {CUSTOMER_LOGOS.map((f) => (
                <div key={f} className="flex items-center justify-center bg-white p-2" style={{ border: `1px solid ${LINE}` }}>
                  <img src={`/proposal/customers/${f}.png`} alt="Customer logo" className="max-h-full max-w-full object-contain" loading="lazy" />
                </div>))}
            </div>
            <p className="mt-4" style={{ ...COND, fontWeight: 700, fontSize: 20, letterSpacing: '.08em', color: BLUE }}>AND MANY MORE…</p>
          </section>

          {/* 06 Partners */}
          <section style={breakBefore}>
            <Heading n={6} eyebrow="Certified technology partners of ADISYS" title="Certified Partners" />
            <img src="/proposal/partners.png" alt="Certified technology partners" className="block w-full" />
            <p className="mt-4" style={{ ...COND, fontWeight: 700, fontSize: 20, letterSpacing: '.08em', color: BLUE }}>AND MANY MORE…</p>
          </section>

          {/* 08 Commercial offer */}
          <section style={breakBefore}>
            <Heading n={secOffer} eyebrow="Technical & commercial BOQ" title={String(p.offerTitle || 'Commercial Offer')} />
            <div className="mb-3 flex items-center px-2.5 py-2 text-[11px] text-white" style={{ ...COND, background: NAVY, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>
              <span className="w-10">Sl. No.</span><span className="flex-1">Product description</span>
              <span className="w-14">Unit</span><span className="w-12">Qty</span><span className="w-24">Make</span>
            </div>
            <div className="space-y-3">
              {lines.map((l) => {
                const specs = String(l.specifications ?? '').split('\n').map((s) => s.replace(/^\s*[-•·*]\s*/, '').trim()).filter(Boolean);
                const half = Math.ceil(specs.length / 2);
                return (
                  <div key={l.lineNo} className="avoid" style={{ border: `1px solid ${LINE}` }}>
                    <div className="flex items-start px-2.5 py-2.5" style={{ background: SOFT }}>
                      <span className="w-10 pt-0.5" style={{ ...COND, fontWeight: 700, fontSize: 20, color: BLUE }}>{String(l.lineNo).padStart(2, '0')}</span>
                      <div className="flex-1 pr-3">
                        <div style={{ ...COND, fontWeight: 700, fontSize: 17, textTransform: 'uppercase', color: NAVY }}>{l.description}</div>
                        <div className="text-[10.5px]" style={{ color: MUTED }}>
                          {l.kind === 'product' ? 'Supply, installation, testing and commissioning' : 'Service'}{l.modelNumber ? ` · Model: ${l.modelNumber}` : ''}</div>
                      </div>
                      <span className="w-14 pt-1 text-[12px]">{l.unit}</span>
                      <span className="tabular w-12 pt-1 text-[12px] font-bold">{qtyLabel(l.quantity)}</span>
                      <span className="w-24 pt-1 text-[12px]">{l.brandName ?? ''}</span>
                    </div>
                    {specs.length > 0 && (
                      <div className="grid grid-cols-2 gap-x-6 py-2.5 pl-[11mm] pr-3 text-[10.5px] leading-snug">
                        {[specs.slice(0, half), specs.slice(half)].map((col, k) => (
                          <ul key={k} className="space-y-1">{col.map((s, i) => (
                            <li key={i} className="flex gap-1.5"><span style={{ color: SKY }}>-</span><span>{s}</span></li>))}</ul>))}
                      </div>)}
                    <div className="flex text-right" style={{ borderTop: `1px solid ${LINE}` }}>
                      <div className="flex-1 px-3 py-2"><div className="text-[8.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Unit price</div>
                        <div className="tabular text-[12px] font-semibold">₹ {fmt(l.unitPrice)}</div></div>
                      {hasDiscount && <div className="flex-1 px-3 py-2" style={{ background: SOFT }}><div className="text-[8.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Discount</div>
                        <div className="tabular text-[12px] font-semibold">₹ {fmt(l.discount)}</div></div>}
                      <div className="flex-1 px-3 py-2" style={{ background: hasDiscount ? undefined : SOFT }}><div className="text-[8.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Taxable value</div>
                        <div className="tabular text-[12px] font-semibold">₹ {fmt(l.taxable)}</div></div>
                      <div className="flex-1 px-3 py-2" style={{ background: hasDiscount ? SOFT : undefined }}><div className="text-[8.5px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>GST {l.gstRate}%</div>
                        <div className="tabular text-[12px] font-semibold">₹ {fmt(l.gst ?? Number(l.total) - Number(l.taxable))}</div></div>
                      <div className="flex-[1.3] px-3 py-2 text-white" style={{ background: NAVY }}><div className="text-[8.5px] font-semibold uppercase tracking-wider" style={{ color: '#9FB0C4' }}>Total amount</div>
                        <div className="tabular text-[14px] font-bold">₹ {fmt(l.total)}</div></div>
                    </div>
                  </div>);
              })}
            </div>

            <div className="avoid mt-5 flex items-start justify-between gap-6">
              <div className="max-w-[55%]">
                <p className="text-[9.5px] font-bold uppercase tracking-wider" style={{ color: MUTED }}>Amount in words</p>
                <p className="text-[12.5px] font-semibold">{amountInWords(q.grandTotal)}</p>
              </div>
              <dl className="w-64 space-y-1 text-[12px]">
                <div className="flex justify-between"><dt>Goods subtotal</dt><dd className="tabular">{fmt(q.goodsSubtotal)}</dd></div>
                {Number(q.discountAmount) > 0 && <div className="flex justify-between"><dt>Discount</dt><dd className="tabular">- {fmt(q.discountAmount)}</dd></div>}
                <div className="flex justify-between"><dt>Taxable value</dt><dd className="tabular">{fmt(q.taxableTotal)}</dd></div>
                <div className="flex justify-between"><dt>GST</dt><dd className="tabular">{fmt(q.gstTotal)}</dd></div>
                <div className="flex justify-between px-2.5 py-2 text-[14px] font-bold text-white" style={{ background: NAVY }}><dt>Grand total (INR)</dt><dd className="tabular">{fmt(q.grandTotal)}</dd></div>
              </dl>
            </div>
            {p.budgetaryNote && <p className="mt-4 font-bold text-red-600">Note: {p.budgetaryNote}</p>}
            {q.notes && <p className="mt-3 whitespace-pre-line">{q.notes}</p>}
          </section>

          {/* 09 Support and scope */}
          <section style={breakBefore}>
            <Heading n={secOffer + 1} eyebrow="Implementation, support & customer scope" title="On-Site Support & Customer Scope" />
            <div className="grid gap-7" style={{ gridTemplateColumns: '1.25fr 1fr' }}>
              <div>
                <div className="mb-1 px-3.5 py-2.5 text-white" style={{ ...COND, background: NAVY, fontWeight: 700, fontSize: 16, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                  A &nbsp;·&nbsp; ADISYS implementation &amp; post-completion support</div>
                {(p.onsiteSupport ?? []).map((t: string, i: number) => <IconRow key={i} icon={SUPPORT_ICONS[i % SUPPORT_ICONS.length]!}>{t}</IconRow>)}
                {p.importantNote && <IconRow icon={Zap}><b>{p.importantNote}</b></IconRow>}
              </div>
              <div>
                <div className="mb-1 px-3.5 py-2.5 text-white" style={{ ...COND, background: BLUE, fontWeight: 700, fontSize: 16, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                  B &nbsp;·&nbsp; Customer scope</div>
                {(p.customerScope ?? []).map((t: string, i: number) => <IconRow key={i} icon={Check}>{t}</IconRow>)}
              </div>
            </div>
          </section>

          {/* 10 Terms, conclusion, signature */}
          <section style={breakBefore}>
            <Heading n={secOffer + 2} eyebrow="Business terms & conditions" title="Terms, Conclusion & Contact" />
            <div>
              {terms.map((t, i) => (
                <div key={i} className="avoid flex gap-4 py-2" style={{ borderBottom: `1px solid ${LINE}` }}>
                  <span className="w-7 shrink-0" style={{ ...COND, fontWeight: 700, fontSize: 17, color: BLUE }}>{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-[11.5px] leading-snug">{t.label && <b>{t.label}: </b>}{t.text}</p>
                </div>))}
            </div>

            <div className="mt-6">
              <Label>Conclusion</Label>
              <div className="space-y-2.5 whitespace-pre-line text-[12px]">{conclusion}</div>
            </div>

            <div className="avoid mt-7 flex items-end justify-between gap-6">
              <div className="text-[12px] leading-snug">
                <p>Thanks &amp; Regards,</p>
                {/* Handwritten signature: public/proposal/signatures/<account manager's email>.png; blank space if none */}
                {sp?.email && !noSig ? (
                  <img src={`/proposal/signatures/${encodeURIComponent(String(sp.email).toLowerCase())}.png`} alt={`Signature of ${sp.name}`}
                    className="my-1 h-[18mm] w-auto object-contain object-left" onError={() => setNoSig(true)} />
                ) : <div className="h-12" />}
                {sp ? (
                  <>
                    <p className="font-bold">{sp.name}</p>
                    {sp.designation && <p>{sp.designation}</p>}
                    <p>{name}</p>
                    {p.address && <p>{p.address}</p>}
                    {sp.phone && <p>Mobile: {sp.phone}</p>}
                    {sp.email && <p>Email: <span style={{ color: BLUE }}>{sp.email}</span></p>}
                    <p>Web: <span style={{ color: BLUE }}>{website}</span></p>
                  </>
                ) : <p style={{ color: MUTED }}>{name}</p>}
              </div>
              <div className="px-6 py-5 text-white" style={{ background: NAVY, maxWidth: '58mm' }}>
                <div style={{ ...COND, fontWeight: 700, fontSize: 28, textTransform: 'uppercase' }}>Thank you</div>
                <p className="mt-1 text-[11.5px] leading-snug" style={{ color: '#C7D0DB' }}>We look forward to partnering with {c.company ?? 'you'}.</p>
              </div>
            </div>
          </section>

        </td></tr></tbody>
      </table>
    </article>
  );
}
